import React from 'react';
import { Navigate, useLocation } from 'react-router-dom';
import { useAuth } from '../contexts/auth/auth-context';
import { AppLoader } from '../components/app-loader/app-loader';
import { useAppBoot } from '../contexts/app-boot-context';
import { readStoredTokens } from '../contexts/auth/helpers';

const authedTargetPath = (profile: any): '/' | '/onboarding' | '/chat' => {
  const registrationCompleted = profile?.registration_completed === true;
  const onboardingCompleted = profile?.onboarding_completed === true;

  if (!registrationCompleted && !onboardingCompleted) return '/';
  if (registrationCompleted && !onboardingCompleted) return '/onboarding';
  if (registrationCompleted && onboardingCompleted) return '/chat';

  return '/';
};

export const RedirectIfAuthenticated: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { profile } = useAuth();
  const { authed, bootLoading } = useAppBoot();
  const location = useLocation();

  const effectiveAuthed = authed || (() => {
    if (!profile) return false;
    const tokens = readStoredTokens();
    return !!tokens.access_token && !!tokens.refresh_token;
  })();

  // Not authed: redirect "/" to "/login" unless ?register param is present
  if (!effectiveAuthed) {
    const hasRegisterParam = new URLSearchParams(location.search).has('register');
    if (location.pathname === '/' && !hasRegisterParam) {
      return <Navigate to="/login" replace />;
    }

    return (
      <>
        <AppLoader active={bootLoading} label="Loading" />
        {children}
      </>
    );
  }

  // Authed: wait for unified boot
  if (bootLoading) {
    return <AppLoader active={true} label="Loading" />;
  }

  // Boot completed but profile failed to load — show login UI
  if (!profile) {
    return <>{children}</>;
  }

  // Check for a deep link redirect (from push notification or expired session)
  const redirectParam = new URLSearchParams(location.search).get('redirect');
  const pushRedirect = (() => {
    try { return sessionStorage.getItem('push_redirect'); } catch { return null; }
  })();
  const pendingRedirect = redirectParam || pushRedirect;

  // Clean up sessionStorage fallback if it was used
  if (pushRedirect) {
    try { sessionStorage.removeItem('push_redirect'); } catch { /* ignore */ }
  }

  // If there's a valid same-origin redirect, go there instead of the default target
  if (pendingRedirect && pendingRedirect.startsWith('/')) {
    return <Navigate to={pendingRedirect} replace />;
  }

  const target = authedTargetPath(profile);

  // Special handling: if user is at "/" and has completed registration,
  // redirect to the appropriate target (onboarding or chat)
  if (location.pathname === '/' && profile?.registration_completed) {
    return <Navigate to={target} replace />;
  }

  // If already at target, show children (prevents loops)
  if (location.pathname === target) return <>{children}</>;

  return <Navigate to={target} replace />;
};
